import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LayoutDashboard, LogOut, Menu, ScrollText, ShieldCheck, Users, X } from "lucide-react";
import { toast } from "sonner";
import { hasCapability, useAuth } from "@/context/AuthContext";
import { timeUntil } from "@/lib/format";
import { TypeBadge } from "@/components/shell/Badges";

const NAV = {
  account: [
    { to: "/account", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/account/security", label: "Security", icon: ShieldCheck },
  ],
  hub: [
    { to: "/hub", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/hub/security", label: "Security", icon: ShieldCheck },
    { to: "/hub/people", label: "People", icon: Users, capability: "management.users.read" },
    { to: "/hub/audit", label: "Audit", icon: ScrollText, capability: "audit.read" },
  ],
};

function NavItems({ area, user, onNavigate }) {
  return NAV[area]
    .filter((item) => !item.capability || hasCapability(user, item.capability))
    .map((item) => (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        onClick={onNavigate}
        data-testid={`${area}-nav-${item.label.toLowerCase()}`}
        className={({ isActive }) =>
          `group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-[background-color,color] duration-150 ${
            isActive ? "bg-electric/10 text-electric" : "text-slate-400 hover:bg-white/5 hover:text-white"
          }`
        }
      >
        {({ isActive }) => (
          <>
            <item.icon className="h-4 w-4" strokeWidth={1.75} />
            <span className="font-medium">{item.label}</span>
            {item.capability ? <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-purple-300/80">{item.capability.split(".")[0]}</span> : null}
            {isActive ? <span className="ml-auto h-1.5 w-1.5 rounded-full bg-electric shadow-[0_0_8px_rgba(0,242,253,0.9)]" /> : null}
          </>
        )}
      </NavLink>
    ));
}

export function ShellLayout({ area, title, eyebrow, actions, children }) {
  const { user, session, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  async function handleSignOut() {
    await signOut();
    toast.success("Signed out");
    navigate("/sign-in", { replace: true });
  }

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between px-5 pb-6 pt-6">
        <div>
          <span className="font-display text-xl font-black tracking-[0.18em] text-white">BIZII</span>
          <p className="bz-caption mt-1">{area === "hub" ? "Staff hub" : "Account"}</p>
        </div>
        <button type="button" onClick={() => setOpen(false)} className="bz-btn-ghost lg:hidden" aria-label="Close navigation">
          <X className="h-4 w-4" strokeWidth={1.75} />
        </button>
      </div>
      <nav className="flex-1 space-y-1 px-3">
        <NavItems area={area} user={user} onNavigate={() => setOpen(false)} />
      </nav>
      <div className="border-t border-white/8 p-4">
        <div data-testid={`${area}-user-chip`} className="rounded-lg border border-white/8 bg-graphite-950/70 p-3">
          <p className="truncate text-sm font-medium text-white">{user.display_name}</p>
          <p className="truncate font-mono text-[11px] text-slate-400">{user.email}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <TypeBadge user={user} />
          </div>
        </div>
        <button type="button" onClick={handleSignOut} data-testid={`${area}-signout-button`} className="bz-btn-ghost mt-3 w-full justify-start text-slate-400">
          <LogOut className="h-4 w-4" strokeWidth={1.75} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-graphite-950 lg:grid lg:grid-cols-[260px_1fr]">
      <aside className="hidden border-r border-white/8 bg-graphite-900 lg:sticky lg:top-0 lg:block lg:h-screen">{sidebar}</aside>
      {open ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button type="button" aria-label="Close navigation" className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 border-r border-white/10 bg-graphite-900 shadow-2xl">{sidebar}</aside>
        </div>
      ) : null}
      <div className="flex min-h-screen flex-col">
        <header className="sticky top-0 z-30 flex items-center justify-between gap-4 border-b border-white/8 bg-graphite-950/80 px-5 py-4 backdrop-blur-xl sm:px-8">
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setOpen(true)} className="bz-btn-ghost lg:hidden" aria-label="Open navigation" data-testid={`${area}-menu-button`}>
              <Menu className="h-5 w-5" strokeWidth={1.75} />
            </button>
            <div>
              {eyebrow ? <p className="bz-caption">{eyebrow}</p> : null}
              <h1 className="font-display text-lg font-bold tracking-tight text-white sm:text-xl">{title}</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {actions}
            <div data-testid={`${area}-session-indicator`} className="hidden items-center gap-2 rounded-full border border-white/10 bg-graphite-900 px-3 py-1.5 text-xs text-slate-400 sm:flex">
              <span className="bz-live-dot h-1.5 w-1.5 rounded-full bg-electric" />
              <span className="font-mono">Session · {timeUntil(session.expires_at)}</span>
            </div>
          </div>
        </header>
        <motion.main
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="flex-1 px-5 py-8 sm:px-8 lg:px-10 lg:py-10"
        >
          <div className="mx-auto w-full max-w-6xl">{children}</div>
        </motion.main>
      </div>
    </div>
  );
}
